import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Award, Wallet, Sparkles, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=1920&q=80";

export default function Home() {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/deals?sort=newest").then(r => setFeatured(r.data.items.slice(0,6))).catch(()=>{});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (zip) params.set("zip_code", zip);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden kn-grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="Massage" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F7F3]/55"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl kn-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-[#E8E2D6] text-xs font-bold uppercase tracking-[0.2em] text-[#2C352D] mb-8">
              <Sparkles className="w-3.5 h-3.5 text-[#D97757]" /> Therapists keep 100%
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-[#2C352D]">
              The unhurried way<br/>to find a <em className="bg-gradient-to-r from-[#0F766E] to-[#D97757] bg-clip-text text-transparent not-italic font-medium">great</em> massage.
            </h1>
            <p className="mt-6 text-lg text-[#2C352D]/80 max-w-2xl leading-relaxed">
              Discover honest deals from local therapists and studios. No commissions, no cuts — just a fair monthly flat fee for them, real savings for you.
            </p>

            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5F6C60]" strokeWidth={1.5} />
                <Input
                  data-testid="hero-zip-input"
                  value={zip}
                  onChange={(e)=>setZip(e.target.value)}
                  placeholder="Enter ZIP code"
                  className="h-14 pl-11 bg-white border-[#E8E2D6] rounded-full text-base"
                />
              </div>
              <Button data-testid="hero-search-button" type="submit" className="h-14 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-8 text-base">
                <Search className="w-4 h-4 mr-2" /> Find deals
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: Wallet, label: "Flat fee. Forever.", body: "Therapists pay $49.99 / business $99.99 per listed deal. We take zero from the deal itself.", color: "#0F766E" },
          { icon: Award, label: "Real local pros", body: "Independent therapists and studios — not faceless chains. Booking happens directly with them.", color: "#D97757" },
          { icon: Sparkles, label: "Calm, not chaos", body: "No bait-and-switch coupons. Every deal is honored on the therapist's own terms.", color: "#0F766E" },
        ].map((it) => (
          <div key={it.label} className="bg-white p-8 rounded-2xl border border-[#E8E2D6] kn-card-hover">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${it.color}15` }}>
              <it.icon className="w-5 h-5" strokeWidth={1.5} style={{ color: it.color }} />
            </div>
            <h3 className="mt-5 font-serif-display text-2xl tracking-tight">{it.label}</h3>
            <p className="mt-2 text-[#5F6C60] leading-relaxed">{it.body}</p>
          </div>
        ))}
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0F766E] py-5 overflow-hidden">
        <div className="flex gap-24 whitespace-nowrap font-serif-display text-lg text-[#F9F7F3] animate-[marquee_40s_linear_infinite]">
          {Array.from({length:2}).map((_,i) => (
            <div key={i} className="flex gap-24">
              <span>Swedish</span><span>·</span><span>Deep Tissue</span><span>·</span><span>Sports</span><span>·</span><span>Hot Stone</span><span>·</span><span>Thai</span><span>·</span><span>Prenatal</span><span>·</span><span>Reflexology</span><span>·</span><span>Aromatherapy</span><span>·</span>
            </div>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0)} to { transform: translateX(-50%)}}`}</style>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Just listed</div>
            <h2 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight">Fresh deals near you</h2>
          </div>
          <Link to="/browse" data-testid="see-all-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium hover:text-[#D97757]">See all <ArrowRight className="w-4 h-4" /></Link>
        </div>

        {featured.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E2D6]">
            <p className="font-serif-display text-2xl text-[#2C352D]">No deals just yet</p>
            <p className="text-[#5F6C60] mt-2">Be the first therapist on Massage Savings.</p>
            <Link to="/register"><Button className="mt-6 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-8">List your deal</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((d) => <DealCard key={d.id} deal={d} />)}
          </div>
        )}
      </section>

      {/* CTA for therapists */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-[#0F766E] text-[#F9F7F3] p-10 lg:p-16">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#D97757]/30 blur-3xl"></div>
          <div className="absolute -left-10 -bottom-20 w-72 h-72 rounded-full bg-[#0C5F58]/60 blur-3xl"></div>
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A373]">For therapists & studios</div>
              <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl leading-tight">Keep every dollar of your deal.</h2>
              <p className="mt-4 text-[#F9F7F3]/80 leading-relaxed max-w-xl">A flat monthly fee — that's it. List as many deals as you'd like, get discovered, and let customers book however you want them to.</p>
              <Link to="/register"><Button data-testid="cta-list-deal" className="mt-8 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-8 h-12">List a deal</Button></Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <PriceCard tier="Individual" price="$49.99" desc="per deal / month" />
              <PriceCard tier="Business" price="$99.99" desc="per deal / month" featured />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PriceCard({ tier, price, desc, featured }) {
  return (
    <div className={`rounded-2xl p-6 border ${featured ? 'bg-[#D97757] border-[#D97757] text-white' : 'bg-[#F9F7F3]/5 border-[#F9F7F3]/15 text-[#F9F7F3]'}`}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">{tier}</div>
      <div className="mt-4 font-serif-display text-4xl">{price}</div>
      <div className="opacity-75 text-sm mt-1">{desc}</div>
    </div>
  );
}

export function DealCard({ deal }) {
  const fallback = "https://images.pexels.com/photos/3997989/pexels-photo-3997989.jpeg?auto=compress&cs=tinysrgb&w=940";
  const pct = Math.max(0, Math.round((1 - deal.deal_price / deal.original_price) * 100));
  return (
    <Link to={`/deals/${deal.id}`} data-testid={`deal-card-${deal.id}`} className="block group">
      <div className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden kn-card-hover">
        <div className="aspect-[4/3] overflow-hidden bg-[#E8E2D6]">
          <img src={deal.image_url || fallback} alt={deal.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-[#0F766E]/10 text-[#0F766E] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{deal.category}</span>
            {pct > 0 && <span className="bg-[#D97757] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{pct}% off</span>}
          </div>
          <h3 className="font-serif-display text-xl leading-tight tracking-tight">{deal.title}</h3>
          <p className="text-sm text-[#5F6C60] mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/> {deal.city}, {deal.state}</p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-serif-display text-3xl text-[#2C352D]">${deal.deal_price.toFixed(0)}</span>
            <span className="text-sm text-[#5F6C60] line-through">${deal.original_price.toFixed(0)}</span>
            <span className="ml-auto text-xs text-[#5F6C60]">{deal.duration_minutes} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
